import { IconNames } from "../common/icon/LucideIcon";
import { Bridge, BridgeType } from "./Bridges";
import { DataSourceStatus as DataSourceStatusHistory } from "./DataSourceStatus";
import {
  IEntryRecordParametersTranslator,
  IEntryRecordLabels,
  IGenericRequestParametersTranslator,
  IModificationRecordLabels,
  IModifiedRecordParametersTranslator,
  INamedEntityLabels,
  INamedEntityParametersTranslator,
  IPaginationParametersTranslator,
  IPermissionedEntityLabels,
  IRelatedEntityLabels,
  PropertyTranslator,
  IUniqueEntityParametersTranslator,
  IUniqueEntityLabels,
} from "./GenericTranslator";
import {
  EntryRecordOrderBy,
  ModificationRecordOrderBy,
  GenericEntityOrderBy,
  EntityConstants,
  ICreatedRecordParameters,
  IEntryRecord,
  IEntityMinimalModel,
  IGenericRequestParameters,
  IModelWithIntId,
  IModificationRecord,
  IModifiedRecordParameters,
  INamedEntity,
  INamedEntityParameters,
  IPaginationParameters,
  IPermissionedEntity,
  IRelatedEntity,
  IRelation,
  IRelationModel,
  IUniqueEntity,
  IUniqueEntityParameters,
  NamedEntityOrderBy,
} from "./GenericTypes";
import { DataFormat } from "./DataFormats";
import { Dataset } from "./Datasets";

export const dataSourceConstants: EntityConstants<DataSource, DataSourceFilters> = {
  resource: "data_sources",
  frontendIndexRoute: "data_sources",
  entitySingular: "data source",
  entityPlural: "data sources",
  icon: "database-backup",
};
export interface DataSourceLastRunStatus {
  isConnected: boolean;
  nextScheduledDate: string;
  lastClientStatus?: DataSourceStatusHistory;
}
export interface FileExcludePattern {
  name: string;
  regex: string;
}
export type DataSourceType = "Crawler" | "IconNMR";
export const DataSourceTypes = ["Crawler", "IconNMR"] as DataSourceType[];
export const DataSourceTypeFormValues = DataSourceTypes.map((t) => ({ id: t, name: t }));

export type DataSourceUpdateMode = "Deactivated" | "UseHash" | "UsePath";
export const DataSourceUpdateModes = ["Deactivated", "UseHash", "UsePath"] as DataSourceUpdateMode[];
export const DataSourceUpdateModesTranslator: { [key in DataSourceUpdateMode]: string } = {
  Deactivated: "Deactivated",
  UseHash: "Update based on file identity",
  UsePath: "Update based on dataset location",
} as const;
export const DataSourceUpdateModesIconTranslator: { [key in DataSourceUpdateMode]: IconNames } = {
  Deactivated: "folder-lock",
  UseHash: "hash",
  UsePath: "folder-tree",
} as const;
export const DataSourceUpdateModeFormValues = DataSourceUpdateModes.map((t) => ({
  id: t,
  name: DataSourceUpdateModesTranslator[t],
}));

export interface DataSource
  extends
    IPermissionedEntity,
    IModelWithIntId,
    INamedEntity,
    IEntryRecord,
    IModificationRecord,
    IRelatedEntity<DataSourceRelations>,
    IUniqueEntity {
  type: DataSourceType;
  enabled: boolean;
  bridge: Bridge;
  format: IEntityMinimalModel<string>;
  directories: string[];
  intervalInSeconds: number;
  cutoffDate?: string | null;
  customImport?: IEntityMinimalModel<string>;
  parserDefinitions: {
    [parserId: string]: DataFormat;
  };
  status?: DataSourceLastRunStatus;
  fileExcludePatterns?: FileExcludePattern[];
  datasetTemplate?: Partial<Dataset>;
  datasetUpdateMode: DataSourceUpdateMode;
  monitoringOnly: boolean;
}
export interface DataSourceSuggestions extends IEntityMinimalModel<DataSource["id"]> {}

export interface DataSourceFilters
  extends
    IGenericRequestParameters<DataSource, DataSourceSortingOptions>,
    IPaginationParameters,
    ICreatedRecordParameters,
    IModifiedRecordParameters,
    INamedEntityParameters,
    IUniqueEntityParameters {
  enabled?: boolean | null;
  bridgeIds?: number[] | null;
  bridgeTypes?: BridgeType[] | null;
  datasetIds?: number[] | null;
  formatIds?: string[] | null;
  customImportIds?: string[] | null;
  directories?: string[] | null;
  sourceHostnames?: string[] | null;
  sourceIpAddresses?: string[] | null;
  types?: DataSourceType[] | null;
  includeStatusHistory?: boolean | null;
}

export const DataSourceFiltersTranslator: PropertyTranslator<DataSourceFilters> = {
  ...IPaginationParametersTranslator,
  ...IGenericRequestParametersTranslator,
  ...INamedEntityParametersTranslator,
  ...IEntryRecordParametersTranslator,
  ...IModifiedRecordParametersTranslator,
  ...IUniqueEntityParametersTranslator,
  enabled: "Is enabled",
  formatIds: "Data formats",
  customImportIds: "Metadata mappings",
  directories: "Directories",
  bridgeIds: "Bridge IDs",
  bridgeTypes: "Bridge types",
  datasetIds: "Dataset IDs",
  sourceHostnames: "Bridge Hostnames",
  sourceIpAddresses: "Bridge IP-Address(es)",
  types: "Types",
  includeStatusHistory: "Include status history",
} as const;

export const DataSourceSortingOptionsConsts = [
  ...GenericEntityOrderBy,
  ...NamedEntityOrderBy,
  ...EntryRecordOrderBy,
  ...ModificationRecordOrderBy,
  "BRIDGE",
  "BRIDGE_TYPE",
  "TYPE",
  "INTERVAL",
  "ENABLED",
] as const;

export interface DataSourceRelations extends IRelationModel {
  datasets: IRelation;
}

export type DataSourceSortingOptions = (typeof DataSourceSortingOptionsConsts)[number];

export const DataSourceFieldLabels: PropertyTranslator<DataSource> & { nextScheduledDate: string } = {
  ...INamedEntityLabels,
  ...IPermissionedEntityLabels,
  ...IEntryRecordLabels,
  ...IModificationRecordLabels,
  ...IRelatedEntityLabels,
  ...IUniqueEntityLabels,
  bridge: "Bridge",
  cutoffDate: "Cut-off date",
  customImport: "Metadata template",
  directories: "Directories",
  enabled: "Enabled",
  format: "Data format",
  id: "Data source ID",
  intervalInSeconds: "Interval",
  parserDefinitions: "Data format definitions",
  status: "Status",
  type: "Source type",
  fileExcludePatterns: "Exclude files",
  nextScheduledDate: "Next scheduled run",
  datasetTemplate: "Default dataset values",
  datasetUpdateMode: "Update behavior",
  monitoringOnly: "Monitoring only",
} as const;
// Client
export interface AutoloadClient {
  id: string; //GUID
  connectionId: string;
  host: string;
  clientVersion: number;
  ipAddress: string;
  firstSeen: string;
  executablePath?: string;
  operatingSystem?: string;
  isApproved: boolean;
  bridgeId: number;
}
